import React, { useState, useEffect, useMemo } from 'react';
import { 
    Card, 
    Button, 
    Space, 
    Tabs, 
    Tag,
    message,
    Spin,
    Empty,
    Typography,
    Modal,
    Table
} from 'antd';
import { ArrowLeftOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import settlementApi from '../../../api/settlementApi';
import { 
    ExchangeRateDisplay,
    BatchStatusBadge,
    BatchTypeTag,
    RouteInstanceTable
} from './components';
import UnrecognizedCostsTab from './components/UnrecognizedCostsTab';

const { Text } = Typography;

/**
 * 批次核算详情页
 */
const BatchCalculationDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [batchData, setBatchData] = useState(null);
    const [selectedChildId, setSelectedChildId] = useState(null);
    // 加载批次详情
    const loadBatchDetail = async () => {
        try {
            setLoading(true);
            const response = await settlementApi.batchCalculations.getBatchCalculation(id);
            setBatchData(response.data);
        } catch (error) {
            console.error('加载批次详情失败:', error);
            message.error('加载批次详情失败: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    // 刷新批次
    const handleRefresh = () => {
        if (!batchData?.batch) return;
        
        Modal.confirm({
            title: '确认刷新批次',
            content: (
                <div>
                    <p>刷新批次将删除旧批次并重新创建，包括：</p>
                    <ul>
                        <li>重新查询航班数据</li>
                        <li>重新执行航线匹配</li>
                        <li>重新聚合成本</li>
                        <li>更新汇率快照</li>
                    </ul>
                    <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                        <strong>注意：</strong>批次ID会变化（分配新ID），但名称、类型、时间范围等配置信息保持不变。
                    </p>
                    <p>确定要刷新批次 "<strong>{batchData.batch.batch_name}</strong>" 吗？</p>
                </div>
            ),
            okText: '确认刷新',
            cancelText: '取消',
            width: 600,
            onOk: async () => {
                try {
                    message.loading({ content: '正在刷新批次（删除重建中）...', key: 'refresh', duration: 0 });
                    
                    const response = await settlementApi.batchCalculations.refreshBatchCalculation(id);
                    
                    message.success({ 
                        content: '批次刷新已启动，正在跳转到新批次...', 
                        key: 'refresh',
                        duration: 2
                    });
                    
                    // 批次ID变化了，跳转到新批次详情页
                    const newBatchId = response.data.batch_id;
                    setTimeout(() => {
                        navigate(`/settlement/batch-calculations/${newBatchId}`);
                    }, 1500);
                    
                } catch (error) {
                    console.error('刷新批次失败:', error);
                    const errorMsg = error.response?.data?.detail?.message || error.message || '刷新失败';
                    message.error({ content: `刷新批次失败: ${errorMsg}`, key: 'refresh' });
                }
            }
        });
    };

    useEffect(() => {
        loadBatchDetail();
    }, [id]);

    // 注意：解构默认值（= []）只对 undefined 生效，对 null 不生效。
    // 后端 BatchDetailResponse 中 children/route_instances/anomalies 类型为 Optional[List[...]] = None，
    // 当字段未设置时序列化为 JSON null，因此这里必须再用 ?? [] 兜底，避免 null.find 崩溃。
    const {
        batch,
        route_instances: routeInstancesRaw,
        anomalies: anomaliesRaw,
        exchange_rates_snapshot,
        children: childrenRaw,
    } = batchData || {};
    const route_instances = routeInstancesRaw ?? [];
    const anomalies = anomaliesRaw ?? [];
    const children = childrenRaw ?? [];
    const isParent = children.length > 0;
    const isChild = batch?.parent_batch_id != null;

    useEffect(() => {
        if (isParent && children.length > 0 && !selectedChildId) {
            setSelectedChildId(children[0].batch_id);
        }
    }, [isParent, children, selectedChildId]);

    const selectedChild = useMemo(
        () => children.find(c => c.batch_id === selectedChildId),
        [children, selectedChildId]
    );

    const filteredInstances = useMemo(() => {
        if (!isParent || !selectedChild) return route_instances;
        const reg = selectedChild.aircraft_reg;
        return route_instances.filter(inst => inst.flights?.[0]?.aircraft_reg === reg);
    }, [isParent, selectedChild, route_instances]);

    const filteredAnomalies = useMemo(() => {
        if (!isParent || !selectedChild) return anomalies;
        const reg = selectedChild.aircraft_reg;
        return anomalies.filter(a => {
            const regs = new Set();
            [...(a.matched_flights || []), ...(a.unmatched_flights || [])].forEach(f => {
                if (f.aircraft_reg) regs.add(f.aircraft_reg);
            });
            if (a.aircraft_reg) regs.add(a.aircraft_reg);
            return regs.has(reg);
        });
    }, [isParent, selectedChild, anomalies]);

    if (loading) {
        return (
            <div style={{ padding: '100px', textAlign: 'center' }}>
                <Spin size="large" tip="加载中..." />
            </div>
        );
    }

    if (!batchData || !batch) {
        return (
            <div style={{ padding: '16px' }}>
                <Card>
                    <Empty description="批次不存在" />
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px' }}>
            {/* 页面标题 */}
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space size="middle">
                        {isChild ? (
                            <Button 
                                icon={<ArrowLeftOutlined />} 
                                onClick={() => navigate(`/settlement/batch-calculations/${batch.parent_batch_id}`)}
                            >
                                返回父批次
                            </Button>
                        ) : (
                            <Button 
                                icon={<ArrowLeftOutlined />} 
                                onClick={() => navigate('/settlement/batch-calculations')}
                            >
                                返回列表
                            </Button>
                        )}
                        <h2 style={{ margin: 0 }}>
                            {batch.batch_name}
                        </h2>
                        <BatchStatusBadge status={batch.status} />
                        <BatchTypeTag type={batch.type} />
                        {isChild && <Tag color="geekblue">{batch.aircraft_reg}</Tag>}
                        {isParent && <Tag color="purple">父批次</Tag>}
                        <Text type="secondary">{batch.start_date?.split('T')[0]} ~ {batch.end_date?.split('T')[0]}</Text>
                    </Space>
                    {(batch.status === 'COMPLETED' || batch.status === 'NEEDS_RECALCULATION') && (
                        <Button 
                            type="primary"
                            icon={<SyncOutlined />}
                            onClick={handleRefresh}
                        >
                            {isChild ? '刷新子批次' : '刷新批次'}
                        </Button>
                    )}
                </div>
            </Card>


            {isParent && (
                <Card title={`✈️ 子批次 (${children.length} 架飞机)`} style={{ marginBottom: 16 }}>
                    <style>{`
                        .child-batch-table .ant-table-row { cursor: pointer; transition: all 0.2s; }
                        .child-batch-table .ant-table-row:hover td { background: #e6f4ff !important; }
                        .child-batch-table .child-row-selected td { background: #bae0ff !important; border-color: #91caff !important; }
                        .child-batch-table .child-row-selected:hover td { background: #91caff !important; }
                    `}</style>
                    <Table
                        className="child-batch-table"
                        size="small"
                        columns={[
                            { title: '飞机号', dataIndex: 'aircraft_reg', width: 120, align: 'center', render: (v) => v ? <Tag color="geekblue">{v}</Tag> : '-' },
                            { title: '状态', dataIndex: 'status', width: 100, align: 'center', render: (s) => <BatchStatusBadge status={s} size="small" showIcon={false} /> },
                            { title: '航班数', dataIndex: 'total_flights', width: 80, align: 'center' },
                            { title: '完整航线', dataIndex: 'total_complete_routes', width: 90, align: 'center' },
                            { title: '不完整航线', dataIndex: 'total_incomplete_routes', width: 100, align: 'center', render: (v) => v > 0 ? <span style={{ color: '#fa8c16' }}>{v}</span> : v },
                            { title: '未匹配航班', dataIndex: 'total_unmatched_flights', width: 100, align: 'center', render: (v) => v > 0 ? <span style={{ color: '#ff4d4f' }}>{v}</span> : v },
                            { title: '总成本 (USD)', dataIndex: 'total_cost', width: 130, align: 'center', render: (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                            { title: '操作', width: 150, render: (_, child) => (
                                <Space size="small">
                                    <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/settlement/batch-calculations/${child.batch_id}`); }}>查看详情</Button>
                                    {(child.status === 'COMPLETED' || child.status === 'NEEDS_RECALCULATION') && (
                                        <Button type="link" size="small" icon={<SyncOutlined />} onClick={(e) => {
                                            e.stopPropagation();
                                            Modal.confirm({
                                                title: `刷新子批次 [${child.aircraft_reg}]`,
                                                content: `只重新计算飞机 ${child.aircraft_reg} 的数据，不影响其他子批次。`,
                                                onOk: async () => {
                                                    message.loading({ content: '刷新中...', key: 'childRefresh', duration: 0 });
                                                    try {
                                                        await settlementApi.batchCalculations.refreshBatchCalculation(child.batch_id);
                                                        message.success({ content: '子批次刷新完成', key: 'childRefresh' });
                                                        loadBatchDetail();
                                                    } catch (err) {
                                                        message.error({ content: '刷新失败: ' + (err.response?.data?.detail || err.message), key: 'childRefresh' });
                                                    }
                                                }
                                            });
                                        }}>刷新</Button>
                                    )}
                                </Space>
                            )},
                        ]}
                        dataSource={children}
                        rowKey="batch_id"
                        pagination={false}
                        onRow={(record) => ({
                            onClick: () => setSelectedChildId(record.batch_id),
                        })}
                        rowClassName={(record) => record.batch_id === selectedChildId ? 'child-row-selected' : ''}
                    />
                </Card>
            )}

            {/* Tab区域 */}
            <Card>
                <Tabs
                    defaultActiveKey="1"
                    items={[
                        {
                            key: '1',
                            label: `🛫 航线实例${isParent && selectedChild ? ` - ${selectedChild.aircraft_reg}` : ''} (${filteredInstances.length})`,
                            children: (
                                <div>
                                    {filteredInstances.length === 0 && filteredAnomalies.length === 0 ? (
                                        <Empty description={isParent ? "请在上方选择飞机查看航线实例" : "暂无数据"} />
                                    ) : (
                                        <RouteInstanceTable
                                            instances={filteredInstances}
                                            anomalies={filteredAnomalies}
                                        />
                                    )}
                                </div>
                            )
                        },
                        {
                            key: '2',
                            label: '💱 汇率快照',
                            children: (
                                <ExchangeRateDisplay exchangeRatesSnapshot={exchange_rates_snapshot} />
                            )
                        },
                        {
                            key: '3',
                            label: '⚠️ 未识别费用清单',
                            children: (
                                <UnrecognizedCostsTab
                                    batchId={
                                        isParent && selectedChild
                                            ? selectedChild.batch_id
                                            : batch.id
                                    }
                                />
                            )
                        }
                    ]}
                />
            </Card>
        </div>
    );
};

export default BatchCalculationDetailPage;

